// Libraries
import React from 'react';

// Supermove
import {DataLoader, Form, Icon, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useDrawer, useState} from '@supermove/hooks';
import {DocumentItemInputChargePaymentType, DocumentTemplateModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Json} from '@supermove/utils';

// App
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import Drawer from '@shared/design/components/Drawer';
import DrawerWithAction from '@shared/design/components/Drawer/DrawerWithAction';
import DocumentItemKindV2 from '@shared/modules/Document/enums/DocumentItemKindV2';
import {DocumentContentJsonFormType} from '@shared/modules/Document/forms/DocumentTemplateVersionForm';
import DocumentContentJson, {
  DocumentItemWithIndex,
} from '@shared/modules/Document/helpers/DocumentContentJson';
import {SetDocumentContentJsonType} from '@shared/modules/Document/types/DocumentTemplateVersionEditorTypes';
import {UserFlowFormToFormType} from '@shared/modules/UserFlows/forms/UserFlowForm';
import InputChargePaymentEditorAmountField from 'modules/Organization/Settings/Document/components/InputChargePaymentEditorAmountField';

const Container = Styled.View`
`;

const DocumentTemplateChargePaymentItemConfigurationFields = ({
  selectedDocumentItem,
  setDraftDocumentContentJson,
}: {
  selectedDocumentItem: DocumentItemWithIndex;
  setDraftDocumentContentJson: SetDocumentContentJsonType;
}) => {
  const {documentItem, index, sectionIndex, columnIndex} = selectedDocumentItem;

  const documentItemField = `${DocumentContentJson.getDocumentItemsField({
    sectionIndex,
    columnIndex,
  })}.${index}`;

  return (
    <Container>
      <InputChargePaymentEditorAmountField
        documentItem={documentItem as DocumentItemInputChargePaymentType}
        documentItemField={documentItemField}
        setDraftDocumentContentJson={setDraftDocumentContentJson}
      />
    </Container>
  );
};

const DocumentTemplateChargePaymentItemConfigurationBody = ({
  form,
  field,
  documentTemplate,
}: {
  form: Form<{userFlowForm: UserFlowFormToFormType}>;
  field: string;
  documentTemplate: DocumentTemplateModel;
}) => {
  const chargePaymentItemDrawer = useDrawer({
    name: 'Charge Payment Item Drawer',
  });

  const [draftDocumentContentJson, setDraftDocumentContentJson] =
    useState<DocumentContentJsonFormType>(
      Json.toObject(
        documentTemplate.activeDocumentTemplateVersion.documentContentJson,
      ) as DocumentContentJsonFormType,
    );

  const foundChargeItem = DocumentContentJson.findPaymentItem(draftDocumentContentJson, [
    DocumentItemKindV2.INPUT_CHARGE_PAYMENT,
  ]);

  if (!foundChargeItem) {
    return null;
  }

  const chargeItem = foundChargeItem.documentItem as DocumentItemInputChargePaymentType;
  const hasAmount =
    chargeItem.input.metadata.amountFixedInCents ||
    chargeItem.input.metadata.amountPercentageInFraction;

  const handleSave = () => {
    form.setFieldValue(`${field}.documentContentJson`, draftDocumentContentJson);
    chargePaymentItemDrawer.handleClose();
  };

  return (
    <React.Fragment>
      {hasAmount && <Icon source={Icon.CheckCircle} color={colors.green.status} />}
      <Space width={8} />
      <SecondaryButton
        text={'Set Up Document'}
        onPress={chargePaymentItemDrawer.handleOpen}
        isSmall
      />
      <DrawerWithAction
        key={chargePaymentItemDrawer.key}
        isOpen={chargePaymentItemDrawer.isOpen}
        width={Drawer.WIDTH.DEFAULT}
        primaryActionText={'Save'}
        headerText={documentTemplate.name}
        handleSubmit={handleSave}
        handleClose={chargePaymentItemDrawer.handleClose}
        secondaryAction={chargePaymentItemDrawer.handleClose}
      >
        <Container>
          <DocumentTemplateChargePaymentItemConfigurationFields
            selectedDocumentItem={foundChargeItem}
            setDraftDocumentContentJson={setDraftDocumentContentJson as SetDocumentContentJsonType}
          />
        </Container>
      </DrawerWithAction>
    </React.Fragment>
  );
};

const DocumentTemplateChargePaymentItemConfiguration = ({
  form,
  field,
  documentTemplateUuid,
}: {
  form: Form<{userFlowForm: UserFlowFormToFormType}>;
  field: string;
  documentTemplateUuid: string | undefined;
}) => {
  const {data, loading} = useQuery<{
    documentTemplate: DocumentTemplateModel;
  }>(DocumentTemplateChargePaymentItemConfiguration.query, {
    variables: {documentTemplateUuid},
  });

  return (
    <DataLoader loading={loading} data={data}>
      {({loadedData}) => {
        return (
          <DocumentTemplateChargePaymentItemConfigurationBody
            form={form}
            field={field}
            documentTemplate={loadedData.documentTemplate}
          />
        );
      }}
    </DataLoader>
  );
};

DocumentTemplateChargePaymentItemConfiguration.query = gql`
  query DocumentTemplateChargePaymentItemConfiguration($documentTemplateUuid: String!) {
    documentTemplate(uuid: $documentTemplateUuid) {
      id
      name
      activeDocumentTemplateVersion {
        id
        documentContentJson
      }
    }
  }
`;

export default DocumentTemplateChargePaymentItemConfiguration;
